/* Created by Interactive 63 - https://interactive63.com */
/**
 * Markup generation: starts from the stored SVG source and adds the classes, wrapper groups and
 * root attributes the generated CSS relies on. The stored source is never modified.
 */
import { applyWordsMarkup, type WordsBuild } from './words';
import { PROPERTIES } from '../properties/registry';
import type { ExportSettings, Project } from '../model/types';
import type { MorphBuild } from '../morph/morph-track';
import { prefixIds } from './prefix-ids';
import { applyRootAttributes } from './root-attributes';
import { readStyleProperty, removeStyleProperties } from './style-attr';
import { CSS_PROPERTIES } from './values';
import type { AnimatedElement, CompileTarget, CompileWarning } from './types';

const SVG_NS = 'http://www.w3.org/2000/svg';

/** Appends class names to an element, keeping any it already has. */
function addClasses(el: Element, ...names: string[]): void {
  const existing = (el.getAttribute('class') ?? '').split(/\s+/).filter((c) => c !== '');
  el.setAttribute('class', [...existing, ...names].join(' '));
}

/** Whether an element paints a stroke, using its own and inherited attributes and styles. */
function hasStroke(el: Element): boolean {
  for (let current: Element | null = el; current; current = current.parentElement) {
    const style = current.getAttribute('style');
    const value =
      (style ? readStyleProperty(style, 'stroke') : undefined) ?? current.getAttribute('stroke');
    if (value !== null) return value.trim() !== '' && value.trim() !== 'none';
  }
  return false;
}

/**
 * Builds the SVG markup for a target. Animated elements gain `st-eN` classes, elements with a
 * transform attribute get a wrapper group when transform tracks are present, and inline styles
 * that would override track values are cleared.
 */
export function buildMarkup(
  project: Project,
  animated: readonly AnimatedElement[],
  target: CompileTarget,
  exportSettings?: ExportSettings,
  morphs: readonly MorphBuild[] = [],
  words: readonly WordsBuild[] = [],
): { markup: string; warnings: CompileWarning[] } {
  const warnings: CompileWarning[] = [];
  const doc = new DOMParser().parseFromString(project.svg.source, 'image/svg+xml');
  const root = doc.documentElement;

  const byId = new Map<string, Element>();
  for (const el of Array.from(doc.querySelectorAll('[data-st-id]'))) {
    const id = el.getAttribute('data-st-id');
    if (id) byId.set(id, el);
  }

  if (target === 'export') {
    for (const meta of Object.values(project.elements)) {
      if (meta.excludeFromExport) byId.get(meta.id)?.remove();
    }
  }

  for (const item of animated) {
    const el = byId.get(item.id);
    if (!el) continue;
    const hasNonTransform = item.tracks.some((t) => !PROPERTIES[t.property].usesTransform);
    const controlled = item.tracks
      .filter((t) => !item.wrapped || !PROPERTIES[t.property].usesTransform)
      .flatMap((t) => CSS_PROPERTIES[t.property]);

    if (item.tracks.some((t) => t.property === 'blur')) {
      warnings.push({
        code: 'blur-safari',
        message: `Blur on ${item.meta.label} may not show in Safari.`,
        elementId: item.id,
      });
    }
    if (
      item.tracks.some((t) => t.property === 'draw') &&
      !item.tracks.some((t) => t.property === 'stroke') &&
      !hasStroke(el)
    ) {
      warnings.push({
        code: 'draw-no-stroke',
        message: `${item.meta.label} has no stroke, so stroke reveal won't be visible.`,
        elementId: item.id,
      });
    }

    const style = el.getAttribute('style');
    if (style !== null && controlled.length > 0) {
      const remaining = removeStyleProperties(style, controlled);
      if (remaining === '') el.removeAttribute('style');
      else el.setAttribute('style', remaining);
    }

    if (item.wrapped) {
      const wrapper = doc.createElementNS(SVG_NS, 'g');
      wrapper.setAttribute('class', `st-wrap st-${item.id}`);
      el.parentNode?.insertBefore(wrapper, el);
      wrapper.append(el);
      if (hasNonTransform) addClasses(el, `st-${item.id}-self`);
    } else {
      addClasses(el, `st-${item.id}`);
    }
  }

  // The runtime and the editor find a morphing path by its own class, since the animated class
  // may sit on a wrapper. A constant shape just sets the path data.
  for (const build of morphs) {
    const el = byId.get(build.elementId);
    if (!el) continue;
    if (build.constant !== null) {
      el.setAttribute('d', build.constant);
    } else if (build.result.ok) {
      addClasses(el, build.result.data.className);
      if (target === 'export' && build.initial !== null) el.setAttribute('d', build.initial);
    }
  }

  warnings.push(...applyWordsMarkup(byId, words, (id) => project.elements[id]?.label ?? id));

  root.setAttribute('xmlns', SVG_NS);
  const { viewBox } = project.svg;
  root.setAttribute('viewBox', `${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}`);
  addClasses(root, 'st-svg');

  if (target === 'export') {
    for (const el of Array.from(doc.querySelectorAll('[data-st-id]')))
      el.removeAttribute('data-st-id');
    if (exportSettings) {
      const sceneName = exportSettings.sceneName || 'scene';
      if (exportSettings.prefixIds) prefixIds(root, `${sceneName}-`);
      applyRootAttributes(root, project, exportSettings, sceneName);
    }
  }

  return { markup: new XMLSerializer().serializeToString(root), warnings };
}
